/**
 * **추적을 막 시작한 화면으로 들어오는 주소** — 등록 창이 짓고, 브랜드 멘션·세트 화면이 한 번 읽고 지운다.
 *
 * ```
 *   등록 창 ─▶ /service/tracking/brand-mentions?tracked=1&period=365&name=아누아&alert=daily
 *            /service/tracking/target-mentions/{id}?keyword=아누아&tracked=1&period=365&alert=daily
 *   화면    ─▶ 기간을 한 번 그 일수로 · 띠를 세운다 · tracked·period·name·alert 를 주소에서 뗀다
 * ```
 *
 * 🔑 `keyword` 는 **떼지 않는다** — 세트 화면에서 그 말을 고른 채로 두는 칸이다(공유 링크도 그 칸을 쓴다).
 * ⚠️ 주소에는 **공개된 말**만 싣는다 — 브랜드 이름 · 검색어 · 계정 아이디.
 * ⚠️ `'use client'` 를 붙이지 않는다 — 순수 함수다.
 */

import type { TrackAlerts } from './track-register';

/** 화면이 읽어 낸 것 — 띠와 기간이 쓴다 */
export type TrackLanding = {
  /** 띠에 적는 이름 — `name` 이 없으면 `keyword` */
  name: string;
  /** 기간을 이 일수로 한 번 맞춘다 — 없으면 기간을 안 건드린다 */
  periodDays: number | null;
  /** 켜 둔 알림 — 실려 오지 않았으면 `null`(띠가 알림 줄을 안 적는다) */
  alerts: TrackAlerts | null;
};

/**
 * 막 시작한 화면의 주소를 짓는다.
 * @param base 화면 경로(이미 `?` 가 있어도 된다)
 */
export function toTrackLandingHref(
  base: string,
  input: { name?: string; keyword?: string; period?: number; alerts?: TrackAlerts },
): string {
  const params = new URLSearchParams();
  const keyword = String(input.keyword ?? '').trim();
  if (keyword) params.set(PARAM.keyword, keyword);
  params.set(PARAM.tracked, '1');
  if (input.period && input.period > 0) params.set(PARAM.period, String(Math.round(input.period)));
  const name = String(input.name ?? '').trim();
  if (name) params.set(PARAM.name, name);
  if (input.alerts) params.set(PARAM.alert, ALERT_KEYS.filter((key) => input.alerts?.[key]).join(',') || 'none');
  return `${base}${base.includes('?') ? '&' : '?'}${params.toString()}`;
}

/**
 * 주소에서 읽는다.
 * @param get 칸 하나를 읽어 주는 것(`searchParams.get`)
 * @returns `tracked=1` 이 아니면 `null` — 그때 화면은 평소대로다
 */
export function readTrackLanding(get: (key: string) => string | null): TrackLanding | null {
  if (String(get(PARAM.tracked) ?? '').trim() !== '1') return null;
  const days = Number(get(PARAM.period));
  const rawAlert = get(PARAM.alert);
  const on = new Set(String(rawAlert ?? '').split(',').map((part) => part.trim()));
  return {
    name: String(get(PARAM.name) ?? get(PARAM.keyword) ?? '').trim(),
    periodDays: Number.isFinite(days) && days > 0 ? Math.round(days) : null,
    alerts: rawAlert === null ? null : { daily: on.has('daily'), weekly: on.has('weekly'), slack: on.has('slack') },
  };
}

/**
 * 읽고 난 주소 — 이 파일이 실은 칸만 뗀다(`keyword` 와 다른 칸은 남긴다).
 * @param search 지금 주소의 쿼리(`searchParams.toString()`)
 * @returns `?` 없는 쿼리 — 비면 빈 글자
 */
export function stripTrackLanding(search: string): string {
  const params = new URLSearchParams(search);
  [PARAM.tracked, PARAM.period, PARAM.name, PARAM.alert].forEach((key) => params.delete(key));
  return params.toString();
}

/** 주소에 싣는 칸 이름 */
const PARAM = { tracked: 'tracked', period: 'period', name: 'name', keyword: 'keyword', alert: 'alert' } as const;

const ALERT_KEYS: (keyof TrackAlerts)[] = ['daily', 'weekly', 'slack'];
